/**
 * FmCorridorRowsChart — one row per stand: the published basal-area band as a
 * range bar, the statutory floor as a tick and the planned residual as a dot,
 * all on one shared m²/ha scale so stands compare at a glance.
 *
 * Only served values are drawn; a row without a band or residual simply has no
 * mark for it. The plot carries a status glyph per row, never a sentence: the
 * verdict words sit in a key under the plot, once per verdict.
 */
import React from 'react';
import { Bar, CartesianGrid, ComposedChart, ReferenceDot, Tooltip, XAxis, YAxis } from 'recharts';
import type { FmStoryCorridorVerdict } from '../elements/plotChartFields.types';
import type { FmChartSize, FmCorridorRow, FmCorridorRowsChartSpec } from './chartTypes';
import {
  CHART_AXIS_FONT_SIZE,
  CHART_GRID_PROPS,
  CHART_XAXIS_PROPS,
  FM_CHART_TOOLTIP_FRAME,
  xAxisTitle,
} from './FmChartChrome.constants';

import { FmChartFrame } from './FmChartFrame';
import { FmRowLabels } from './FmRowLabels';
import { CORRIDOR_ROW_COLORS } from './chartRoleColors';
import { useChartTooltipIntent } from './chartTooltipIntent';
import './FmCorridorRowsChart.css';

export type FmCorridorRowsChartProps = Omit<FmCorridorRowsChartSpec, 'kind'> & {
  size?: FmChartSize;
  'data-testid'?: string;
};

const ROW_HEIGHT = 56;
const AXIS_ROOM = 48;
const BAND_BAR_SIZE = 14;
const FLOOR_TICK_HALF = 10;

// Two stands may share a label and verdict; the category must stay one row per
// stand, so it is the row id and the label is drawn by FmRowLabels.
function rowKey(row: FmCorridorRow): string {
  return row.id;
}

/** One glyph per verdict; the key under the plot spells each one out. */
const VERDICT_GLYPH: Readonly<Record<string, string>> = {
  inside: '✓',
  'below-band': '▼',
  'above-band': '▲',
  'legal-risk': '!',
  uncovered: '–',
  'no-residual': '○',
};

function verdictGlyph(verdict: string): string {
  return verdict in VERDICT_GLYPH ? VERDICT_GLYPH[verdict as FmStoryCorridorVerdict] : '•';
}

/** Each verdict once, in first-row order, with the words the row served. */
function verdictKey(rows: readonly FmCorridorRow[]): { glyph: string; label: string }[] {
  const seen = new Map<string, string>();
  rows.forEach((row) => {
    if (!seen.has(row.verdictLabel)) seen.set(row.verdictLabel, verdictGlyph(row.verdict));
  });
  return [...seen].map(([label, glyph]) => ({ glyph, label }));
}

interface TickShapeProps {
  cx?: number;
  cy?: number;
}

const FloorTick: React.FC<TickShapeProps> = ({ cx = 0, cy = 0 }) => (
  <line
    x1={cx}
    x2={cx}
    y1={cy - FLOOR_TICK_HALF}
    y2={cy + FLOOR_TICK_HALF}
    stroke={CORRIDOR_ROW_COLORS.floor}
    strokeWidth={2}
    data-testid="corridor-row-floor"
  />
);

const ResidualDot: React.FC<TickShapeProps> = ({ cx = 0, cy = 0 }) => (
  <circle
    cx={cx}
    cy={cy}
    r={6}
    fill={CORRIDOR_ROW_COLORS.residual}
    stroke="var(--fm-chart-halo)"
    strokeWidth={2}
    data-testid="corridor-row-residual"
  />
);

interface CorridorTooltipProps {
  active?: boolean;
  label?: string | number;
  rows: readonly FmCorridorRow[];
}

const CorridorTooltip: React.FC<CorridorTooltipProps> = ({ active, label, rows }) => {
  const engaged = useChartTooltipIntent();
  const row = rows.find((candidate) => rowKey(candidate) === label);
  if (!active || !engaged || !row) return null;
  return (
    <div className="fm-chart-tooltip fm-corridor-rows__tooltip" style={FM_CHART_TOOLTIP_FRAME}>
      <div className="fm-chart-tooltip__title">{row.label}</div>
      <div>{row.detail}</div>
    </div>
  );
};

export const FmCorridorRowsChart: React.FC<FmCorridorRowsChartProps> = ({
  rows,
  xLabel,
  bandLabel,
  formatValue,
  'data-testid': testId,
}) => {
  const data = rows.map((row) => ({
    key: rowKey(row),
    band: row.bandLo !== null && row.bandHi !== null ? [row.bandLo, row.bandHi] : null,
  }));
  return (
    <div className="fm-corridor-rows">
      <FmChartFrame
        size="md"
        height={rows.length * ROW_HEIGHT + AXIS_ROOM}
        data-testid={testId}
        data-kind="corridor-rows"
      >
        <ComposedChart
          data={data}
          layout="vertical"
          margin={{ top: 8, right: 8, bottom: 20, left: 8 }}
          accessibilityLayer
        >
          <CartesianGrid {...CHART_GRID_PROPS} horizontal={false} vertical />
          <XAxis
            type="number"
            domain={[0, 'auto']}
            {...CHART_XAXIS_PROPS}
            tickFormatter={formatValue}
            label={xAxisTitle(xLabel)}
          />
          <YAxis type="category" dataKey="key" hide />
          <Tooltip
            content={<CorridorTooltip rows={rows} />}
            cursor={{ fill: 'var(--fm-d-surface-3)' }}
          />
          <Bar
            dataKey="band"
            name={bandLabel}
            fill={CORRIDOR_ROW_COLORS.band}
            fillOpacity={0.45}
            barSize={BAND_BAR_SIZE}
            isAnimationActive={false}
          />
          {rows.map((row) =>
            row.legalMin === null ? null : (
              <ReferenceDot
                key={`${row.id}-floor`}
                x={row.legalMin}
                y={rowKey(row)}
                shape={<FloorTick />}
                ifOverflow="extendDomain"
              />
            ),
          )}
          {rows.map((row) =>
            row.residual === null ? null : (
              <ReferenceDot
                key={`${row.id}-residual`}
                x={row.residual}
                y={rowKey(row)}
                shape={<ResidualDot />}
                ifOverflow="extendDomain"
              />
            ),
          )}
          <FmRowLabels
            rows={rows.map((row) => ({
              id: row.id,
              category: rowKey(row),
              left: row.label,
              right: verdictGlyph(row.verdict),
              rightLabel: row.verdictLabel,
            }))}
            fontSize={CHART_AXIS_FONT_SIZE}
          />
        </ComposedChart>
      </FmChartFrame>
      <ul className="fm-corridor-rows__key" data-testid={testId ? `${testId}-key` : undefined}>
        {verdictKey(rows).map((entry) => (
          <li key={entry.label}>
            <span className="fm-corridor-rows__glyph" aria-hidden="true">
              {entry.glyph}
            </span>
            {entry.label}
          </li>
        ))}
      </ul>
    </div>
  );
};
